import { useEffect, useMemo, useRef } from 'react';

import * as echarts from 'echarts';

import { BarChartProps } from './EChartsBarChartVerticalDots';
import { chartColors } from '../../configs/chart.config';

export const EChartsBarChartVertical = <T,>({
  data,
  keyField,
  valueField,
  yAxisLabel,
  formatKey,
  valueFormatOptions,
  width = '100%',
  height = '480px',
  className = '',
  chartMargin,
  hideTooltip = false,
}: BarChartProps<T>) => {
  const chartRef = useRef<HTMLDivElement | null>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);

  const chartOption = useMemo(() => {
    if (!data || data.length === 0) return null;

    const getKey = (item: T) =>
      typeof keyField === 'function' ? keyField(item) : (item as any)[keyField];

    const getValue = (item: T) => {
      if (!valueField) return 0;
      if (typeof valueField === 'function') return valueField(item);
      return Number((item as Record<keyof T, unknown>)[valueField as keyof T]);
    };

    const keyValuePairs = data.map((item) => {
      const rawKey = getKey(item);
      const value = getValue(item);
      return {
        key: formatKey ? formatKey(String(rawKey)) : String(rawKey),
        value: value == null ? 0 : Number(value),
      };
    });

    // Trim leading and trailing zeroes
    const firstNonZeroIndex = keyValuePairs.findIndex((item) => item.value !== 0);
    const lastNonZeroIndex =
      keyValuePairs.length - 1 - [...keyValuePairs].reverse().findIndex((item) => item.value !== 0);

    const trimmedData = keyValuePairs.slice(firstNonZeroIndex, lastNonZeroIndex + 1);

    // Extract labels and values
    const labels = trimmedData.map((item) => item.key);
    const values = trimmedData.map((item) => item.value);

    return {
      backgroundColor: '#1F1F1F',
      grid: {
        top: 35,
        left: 50,
        right: 20,
        bottom: 20,
        containLabel: true,
        ...(chartMargin || {}),
      },
      tooltip: hideTooltip
        ? undefined
        : {
            trigger: 'axis',
            axisPointer: {
              type: 'shadow',
            },
            backgroundColor: 'rgba(66,66,66,0.95)',
            borderWidth: 0,
            borderRadius: 4,
            padding: 12,
            textStyle: {
              color: '#fff',
              fontWeight: 'bold',
            },
            formatter: (params: any) => {
              if (!params.length) return '';
              const item = params[0];
              return `
                <div style="padding: 4px 8px; max-width: 300px;">
                  <p style="margin: 0; font-weight: bold; font-size: 12px; color: white;">
                    ${item.name}
                  </p>
                  <p style="margin: 0; font-size: 12px; color: ${item.color};">
                    ${item.seriesName}: ${new Intl.NumberFormat(undefined, valueFormatOptions).format(item.value)}
                  </p>
                </div>
              `;
            },
          },
      xAxis: {
        type: 'category',
        data: labels,
        axisLine: { lineStyle: { color: '#28303E' } },
        axisLabel: {
          rotate: labels.length > 10 ? -300 : 0,
          color: '#9E9E9E',
        },
      },
      yAxis: {
        type: 'value',
        name: yAxisLabel,
        nameLocation: 'middle',
        nameTextStyle: {
          color: '#9E9E9E',
          fontSize: 12,
          padding: [0, 0, 90, 0],
        },
        axisLabel: {
          color: '#9E9E9E',
        },
        splitLine: {
          lineStyle: { color: '#28303E' },
        },
      },
      series: [
        {
          name: yAxisLabel,
          type: 'bar',
          data: values,
          itemStyle: {
            color: (params: any) => {
              const index = params.dataIndex;
              return chartColors.colorPalette[index % chartColors.colorPalette.length];
            },
          },
          barMaxWidth: 32,
        },
      ],
    };
  }, [
    data,
    keyField,
    valueField,
    yAxisLabel,
    formatKey,
    valueFormatOptions,
    chartMargin,
    hideTooltip,
  ]);

  useEffect(() => {
    if (chartRef.current && chartOption) {
      if (!chartInstance.current) {
        chartInstance.current = echarts.init(chartRef.current);
      }
      chartInstance.current.setOption(chartOption);
    }
    return () => {
      chartInstance.current?.dispose();
      chartInstance.current = null;
    };
  }, [chartOption]);

  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center text-slate-500" style={{ width, height }}>
        No data available
      </div>
    );
  }

  return (
    <div className={`relative mx-auto overflow-hidden ${className}`}>
      <div
        ref={chartRef}
        style={{
          width,
          height,
          position: 'relative',
        }}
      />
    </div>
  );
};
